import {
  getCreditBalanceCalcLabel,
  getDebitBalanceCalcLabel,
  getParentAccountCode,
  resolveAccountGroup
} from './helpers';
import { buildDataTableCols, type DataTableCol } from '@/components/custom/erp/data-table-v2';
import type { SearchFieldColumn } from '@/components/custom/erp/search-field-v2/types';
import { Checkbox } from '@/components/ui/checkbox';
import type { AccountModel } from '@/types/schemas';

const renderTextCell = (value: string | number | null | undefined, isTopLevel: boolean) => (
  <span className={isTopLevel ? 'font-semibold' : 'font-normal'}>{value ?? ''}</span>
);

const renderReadonlyCheckbox = (checked: boolean) => (
  <div className='flex h-full items-center justify-center'>
    <Checkbox checked={checked} disabled className='size-4' />
  </div>
);

const isTopLevelAccount = (account: AccountModel) => !getParentAccountCode(account);

const ACCOUNT_COLS: DataTableCol<AccountModel>[] = [
  {
    field: 'code',
    headerName: 'Tài khoản',
    width: 120,
    valueGetter: row => row.code,
    renderCell: params => renderTextCell(params.row.code, isTopLevelAccount(params.row))
  },
  {
    field: 'name',
    headerName: 'Tên tài khoản',
    width: 250,
    valueGetter: row => row.name,
    renderCell: params => renderTextCell(params.row.name, isTopLevelAccount(params.row))
  },
  {
    field: 'parent_account_code',
    headerName: 'Tài khoản mẹ',
    width: 120,
    valueGetter: row => getParentAccountCode(row),
    renderCell: params => renderTextCell(getParentAccountCode(params.row), isTopLevelAccount(params.row))
  },
  {
    field: 'tk_so_cai',
    headerName: 'TK sổ cái',
    width: 100,
    align: 'center',
    headerAlign: 'center',
    valueGetter: row => Boolean(row.tk_so_cai),
    renderCell: params => renderReadonlyCheckbox(Boolean(params.row.tk_so_cai))
  },
  {
    field: 'tk_chi_tiet',
    headerName: 'TK chi tiết',
    width: 100,
    align: 'center',
    valueGetter: row => Boolean(row.tk_chi_tiet),
    renderCell: params => renderReadonlyCheckbox(Boolean(params.row.tk_chi_tiet))
  },
  {
    field: 'bac_tk',
    headerName: 'Bậc TK',
    width: 100,
    align: 'center',
    valueGetter: row => row.bac_tk ?? 0,
    renderCell: params => renderTextCell(params.row.bac_tk ?? 0, isTopLevelAccount(params.row))
  },
  {
    field: 'currency_code',
    headerName: 'Ngoại tệ',
    width: 100,
    align: 'center',
    valueGetter: row => Boolean(row.currency_code),
    renderCell: params => renderReadonlyCheckbox(Boolean(params.row.currency_code))
  },
  {
    field: 'ar_ap_tracking_type',
    headerName: 'TK theo dõi công nợ',
    width: 150,
    align: 'center',
    valueGetter: row => Boolean(row.ar_ap_tracking_type),
    renderCell: params => renderReadonlyCheckbox(Boolean(params.row.ar_ap_tracking_type))
  },
  {
    field: 'account_type',
    headerName: 'Phân loại tài khoản',
    width: 160,
    valueGetter: row => resolveAccountGroup(row.code),
    renderCell: params => renderTextCell(resolveAccountGroup(params.row.code), isTopLevelAccount(params.row))
  },
  {
    field: 'balance_type',
    headerName: 'Loại số dư',
    width: 120,
    valueGetter: row => row.balance_type ?? '',
    renderCell: params => renderTextCell(params.row.balance_type ?? '', isTopLevelAccount(params.row))
  },
  {
    field: 'fx_reval_debit_type',
    headerName: 'PP tính TGGS nợ',
    width: 150,
    valueGetter: row => getDebitBalanceCalcLabel(row),
    renderCell: params => renderTextCell(getDebitBalanceCalcLabel(params.row), isTopLevelAccount(params.row))
  },
  {
    field: 'fx_reval_credit_type',
    headerName: 'PP tính TGGS có',
    width: 150,
    valueGetter: row => getCreditBalanceCalcLabel(row),
    renderCell: params => renderTextCell(getCreditBalanceCalcLabel(params.row), isTopLevelAccount(params.row))
  },
  {
    field: 'active',
    headerName: 'Hoạt động',
    width: 100,
    align: 'center',
    valueGetter: row => Boolean(row.active),
    renderCell: params => renderReadonlyCheckbox(Boolean(params.row.active))
  }
];

const LOOKUP_COLS: DataTableCol<AccountModel>[] = [
  {
    field: 'code',
    headerName: 'Mã tài khoản',
    width: 150,
    valueGetter: row => row.code,
    renderCell: params => renderTextCell(params.row.code, isTopLevelAccount(params.row))
  },
  {
    field: 'name',
    headerName: 'Tên tài khoản',
    width: 250,
    valueGetter: row => row.name,
    renderCell: params => renderTextCell(params.row.name, isTopLevelAccount(params.row))
  },
  {
    field: 'parent_account_code',
    headerName: 'Tài khoản mẹ',
    width: 150,
    valueGetter: row => getParentAccountCode(row),
    renderCell: params => renderTextCell(getParentAccountCode(params.row), isTopLevelAccount(params.row))
  },
  {
    field: 'tk_so_cai',
    headerName: 'TK sổ cái',
    width: 90,
    valueGetter: row => Boolean(row.tk_so_cai),
    renderCell: params => renderReadonlyCheckbox(Boolean(params.row.tk_so_cai))
  },
  {
    field: 'tk_chi_tiet',
    headerName: 'TK chi tiết',
    width: 90,
    valueGetter: row => Boolean(row.tk_chi_tiet),
    renderCell: params => renderReadonlyCheckbox(Boolean(params.row.tk_chi_tiet))
  },
  {
    field: 'bac_tk',
    headerName: 'Bậc TK',
    width: 100,
    valueGetter: row => row.bac_tk ?? 0,
    renderCell: params => renderTextCell(params.row.bac_tk ?? 0, isTopLevelAccount(params.row))
  },
  {
    field: 'balance_type',
    headerName: 'Loại số dư',
    width: 120,
    valueGetter: row => row.balance_type ?? '',
    renderCell: params => renderTextCell(params.row.balance_type ?? '', isTopLevelAccount(params.row))
  },
  {
    field: 'active',
    headerName: 'Hoạt động',
    width: 100,
    valueGetter: row => Boolean(row.active),
    renderCell: params => renderReadonlyCheckbox(Boolean(params.row.active))
  }
];

export const ACCOUNT_TABLE_COLUMNS = buildDataTableCols(ACCOUNT_COLS);
export const ACCOUNT_LOOKUP_COLUMNS = buildDataTableCols(LOOKUP_COLS) as SearchFieldColumn<AccountModel>[];
